import React, { useState, useRef, useCallback, useContext } from 'react';
import { FaChevronLeft, FaChevronRight, FaShoppingCart, FaStar } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { cartContext } from '../components/CartContext';

const PersonalizedRecommendations = ({ 
  title = 'Just For You', 
  subtitle = '',
  products = [], 
  onAddToCart = () => {} 
}) => {
  const scrollRef = useRef(null);
  const { addItemToCart } = useContext(cartContext);

  const scroll = useCallback((direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }, []);

  const handleAddToCart = (product) => {
    const cartItem = {
      productId: product.productId || product.productID,
      productName: product.productName,
      price: product.price,
      quantity: 1,
      productImage: product.productImage,
    };
    addItemToCart(cartItem);
    onAddToCart(product);
  };

   const getProductImageUrl = (productImage) => {
    try {
      // Try parsing as JSON first (legacy format)
      const productImages = JSON.parse(productImage);
      return Array.isArray(productImages) ? productImages[0] : productImage;
    } catch {
      // If parsing fails, it's already a direct URL (new format)
      return productImage;
    }
  };

  const ProductCard = ({ product }) => {
    const discount = (product.discount || 0) / 100;
    const discountedPrice = (product.price * (1 - discount)).toFixed(2);
    const firstImage = getProductImageUrl(product.productImage || (product.imageUrls && product.imageUrls[0]));

    // try {
    //   const parsed = JSON.parse(product.productImage);
    //   imageUrl = Array.isArray(parsed) ? parsed[0] : product.productImage;
    // } catch {
    //   imageUrl = product.productImage;
    // }

    return (
      <div
        key={product.productId}
        className="flex-shrink-0 w-56 bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 group flex flex-col h-full"
      >
        <Link
          to={`/product/${encodeURIComponent(product.productName)}/${product.productId}`}
          className="block flex-grow flex flex-col"
        >
          <div className="relative overflow-hidden bg-gray-50 h-56">
            {firstImage && (
              <img
                src={firstImage}
                alt={product.productName}
                className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                onError={(e) => (e.target.src = '/api/placeholder/224/224')}
              />
            )}
            {discount > 0 && (
              <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                -{(discount * 100).toFixed(0)}%
              </div>
            )}
          </div>

          <div className="p-3 space-y-2 flex-grow flex flex-col">
            <h4 className="text-sm font-medium text-gray-800 line-clamp-2">
              {product.productName}
            </h4>

            <div className="flex items-center gap-1">
              <div className="flex text-yellow-400 text-xs">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className={i < 4 ? '' : 'text-gray-300'} />
                ))}
              </div>
              <span className="text-xs text-gray-600">(128)</span>
            </div>

            <div className="space-y-1 mt-auto">
              {discount > 0 && (
                <div className="text-xs text-gray-400 line-through">
                  KSH {Number(product.price).toLocaleString()}
                </div>
              )}
              <div className="text-lg font-bold text-gray-900">
                KSH {Number(discountedPrice).toLocaleString()}
              </div>
            </div>

            <div className={`text-xs font-semibold ${product.stockQuantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {product.stockQuantity > 0 ? '✓ In Stock' : '⚠ Out of Stock'}
            </div>
          </div>
        </Link>

        {product.stockQuantity > 0 && (
          <button
            onClick={() => handleAddToCart(product)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <FaShoppingCart className="text-sm" />
            <span className="text-sm">Add to Cart</span>
          </button>
        )}
      </div>
    );
  };

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <div className="w-full mb-12">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          {subtitle && (
            <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
        <Link to="/shop-all" className="text-blue-600 font-semibold hover:underline">
          See All →
        </Link>
      </div>

      <div className="relative group">
        <button
          onClick={() => scroll('left')}
          className="absolute -left-5 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg hover:shadow-xl rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300"
        >
          <FaChevronLeft className="text-gray-700" />
        </button>

        <button
          onClick={() => scroll('right')}
          className="absolute -right-5 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg hover:shadow-xl rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300"
        >
          <FaChevronRight className="text-gray-700" />
        </button>

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide"
          style={{ scrollBehavior: 'smooth', scrollbarWidth: 'none' }}
        >
          {products.map((product) => (
            <ProductCard key={product.productId} product={product} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PersonalizedRecommendations;